import { forwardRef } from 'react';
import { Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Company } from '@gokrew/models/company';
import { TranslationNS } from '@gokrew/models/constants';
import styles from './CompanySwitcher.module.css';
import { companyName } from './companyLabels';

interface SwitcherCardProps {
  selected?: Company;
  isCollapsed: boolean;
  multiCompany: boolean;
  shortcutHint: string;
  className?: string;
  flat?: boolean;
  onOpen: () => void;
}

/** The sidebar trigger — the collapsed mark, or the expanded workspace card. */
export const SwitcherCard = forwardRef<HTMLDivElement, SwitcherCardProps>(
  (
    {
      selected,
      isCollapsed,
      multiCompany,
      shortcutHint,
      className,
      flat,
      onOpen,
    },
    ref
  ) => {
    const { t } = useTranslation(TranslationNS.LibsCommon);

    if (isCollapsed) {
      return (
        <div ref={ref} className={`${styles.collapsedWrap} ${className ?? ''}`}>
          <button
            type="button"
            className={styles.collapsedTile}
            onClick={onOpen}
            disabled={!multiCompany}
            title={
              multiCompany
                ? t('company_switcher.switch_workspace_shortcut', {
                    shortcut: shortcutHint,
                  })
                : selected && companyName(selected)
            }
            aria-label={
              (selected && companyName(selected)) ||
              t('company_switcher.switch')
            }
          >
            {/* Company logos never fit this tile nicely; the name (shown in
                the tooltip) identifies the workspace. */}
            <Building2 size={18} aria-hidden="true" />
          </button>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={`${styles.card} ${flat ? styles.cardFlat : ''} ${className ?? ''}`}
      >
        <button
          type="button"
          className={`${styles.cardTop} ${flat ? styles.cardTopFlat : ''}`}
          onClick={onOpen}
          disabled={!multiCompany}
          aria-label={t('company_switcher.switch')}
        >
          <span className={styles.cardText}>
            <span className={styles.cardName}>
              {selected ? companyName(selected) : ''}
            </span>
          </span>
        </button>
      </div>
    );
  }
);

SwitcherCard.displayName = 'SwitcherCard';
